/** TEMU 平台工作区路由常量（店铺类型路径段 + 公用页面） */

import { openUrlInNewTab } from '@/config/ipeRoutes';
import i18n from '@/i18n';
import { bindNavLabels, copyNavItem } from '@/i18n/bindCopy';
import { platformNavLabel } from '@/i18n/platformNavLabel';
import { normalizeTemuShopType } from '@/utils/platformShopCatalog';

export const IPE_TEMU_BASE_PATH = '/ipe/temu';
export const IPE_TEMU_GATE_PATH = `${IPE_TEMU_BASE_PATH}/gate`;
export const TEMU_NOTICE_HELP_PATH = `${IPE_TEMU_BASE_PATH}/help`;

/** URL 类型段 slug */
export const TEMU_SHOP_TYPE_SLUGS = ['full', 'semi', 'local'];

export const TEMU_SHOP_TYPES = ['半托管', '全托管', '本土'];

export const TEMU_ACTIVE_SHOP_STORAGE_KEY = 'ipe_temu_active_shop_id';
export const TEMU_WORKSPACE_TOKEN_KEY = 'ipe_temu_workspace_token';
export const TEMU_WORKSPACE_SHOP_TYPE_KEY = 'ipe_temu_workspace_shop_type';
export const TEMU_SHOP_SWITCH_TOAST_KEY = 'ipe_temu_shop_switch_toast';

/** @deprecated 兼容旧三轨命名 */
export const TEMU_TRACKS = TEMU_SHOP_TYPE_SLUGS;

export const TEMU_PRODUCT_SUBNAV = [
  { id: 'publish', label: '发布产品', path: 'publish', openInNewTab: true },
  { id: 'info', label: '产品信息', path: '' },
  { id: 'records', label: '发布记录', path: 'records' },
  { id: 'drafts', label: '草稿箱', path: 'drafts' },
];

/** 侧栏可见模板子项（建议价资料库仍隐藏，路由保留） */
export const TEMU_TEMPLATE_SUBNAV = [
  { id: 'product', label: '产品模板', path: 'product' },
  { id: 'size-chart', label: '尺码库', path: 'size-chart' },
  { id: 'attributes', label: '属性库', path: 'attributes' },
  { id: 'model', label: '模特库', path: 'model' },
  { id: 'photo', label: '实拍图库', path: 'photo' },
  { id: 'certificates', label: '证书库', path: 'certificates' },
  { id: 'warehouse', label: '仓库模板', path: 'warehouse' },
  { id: 'freight', label: '运费模板', path: 'freight' },
];

/** 半托管发品才需要的模板库子项；全托管/本土侧栏不展示 */
export const TEMU_TEMPLATE_SUBNAV_SEMI_ONLY_IDS = new Set(['warehouse', 'freight']);

/**
 * 模块 × 店铺类型 能力矩阵（唯一事实来源：侧栏显隐 + 路由守卫都以它为准）。
 *
 * <p>依据后端 OpenAPI 的实际分流，避免出现「半托店点开全托模块」导致
 * 平台返回 {@code 7000019 no access} / {@code 7000016 type not exists}，
 * 或接口调通但列表永远为空：
 * <ul>
 *   <li>价格处理（核价/调价）：全托与半托<b>都</b>不可用。平台「自研专属接口」清单里
 *       同时列了全托 {@code bg.price.review.*} / {@code bg.full.adjust.price.*} 与半托
 *       {@code bg.semi.*.order}，仅限自研应用单独申请通过后使用；本系统现有应用均为 ISV，
 *       调用必然 {@code 7000019/7000022}，故整块下线（后端同样由
 *       {@code TemuShopTrack.requirePricingAvailable} 拦截，不会打平台）。</li>
 *   <li>营销活动：跨境活动API组 {@code bg.marketing.activity.*} 属「自研专属接口」，
 *       半托与全托共用同一套 {@code .global} type，故对两者都不可用；
 *       本土走 {@code bg.promotion.activity.*}（L2L）可进营销活动。</li>
 *   <li>备货发货 / 打印中心 / 销量数据 / 包装合规 / 商品广告 / 寄样质检 / 退货退款：仅全托
 *       （后端 {@code TemuPhase2StubController.requireFull}）。</li>
 *   <li>发货仓(站点)与运费模板：只有半托/本土发品链路需要
 *       （后端发品对全托 {@code skipSiteWarehouseFreight}）。</li>
 * </ul>
 *
 * <p>{@code relativePath} 为去掉 {@code /ipe/temu/{slug}/} 之后的部分，取最长前缀匹配。
 */
export const TEMU_TRACK_GATED_PATHS = [
  {
    // 跨境核价/调价属自研专属；本土走 bg.local.goods.priceorder.* 可进价格处理
    prefix: 'discuss',
    slugs: ['local'],
    moduleLabel: '价格处理',
    reason:
      '跨境核价/调价接口属平台「自研专属接口」，ISV 不可用；'
      + '本土店铺可使用 bg.local.goods.priceorder.* 价单能力',
  },
  {
    prefix: 'price',
    slugs: ['local'],
    moduleLabel: '价格处理',
    reason:
      '跨境核价/调价接口属平台「自研专属接口」，ISV 不可用；'
      + '本土店铺可使用 bg.local.goods.priceorder.* 价单能力',
  },
  {
    // 跨境活动API组半托/全托共用 .global 且仅自研；本土走 bg.promotion.activity.*
    prefix: 'marketing',
    slugs: ['local'],
    moduleLabel: '营销活动',
    reason:
      '跨境「活动API组」（bg.marketing.activity.*）仅对商家自研应用开放，ISV 不可用；'
      + '本土店铺可使用 bg.promotion.activity.* 促销活动能力',
  },
  {
    prefix: 'orders/stocking',
    slugs: ['full'],
    moduleLabel: '备货发货',
    reason: '备货发货（bg.purchaseorderv2.get 等）仅全托管店铺可用',
  },
  {
    prefix: 'orders/print',
    slugs: ['full'],
    moduleLabel: '打印中心',
    reason: '打印中心（箱唛/运单标签）仅全托管店铺可用',
  },
  {
    prefix: 'orders/sales',
    slugs: ['full'],
    moduleLabel: '销量数据',
    reason: '销量数据（bg.goods.salesv2.get）仅全托管店铺可用',
  },
  {
    prefix: 'orders/packing',
    slugs: ['full'],
    moduleLabel: '包装合规',
    reason: '包装清单（bg.themis.*）仅全托管店铺可用',
  },
  {
    prefix: 'orders/ads',
    slugs: ['full'],
    moduleLabel: '商品广告',
    reason: '商品广告投放（bg.glo.searchrec.ad.*）仅全托管店铺可用',
  },
  {
    prefix: 'orders/sample-qc',
    slugs: ['full'],
    moduleLabel: '寄样质检',
    reason: '寄样与质检（bg.sample.order.get / bg.goods.qualityinspection.*）仅全托管店铺可用',
  },
  {
    // 半托/本土走 bg.aftersales.* 销售售后；全托走 bg.refund.* 退供包裹；同 URL 按 shopType 分流
    prefix: 'orders/returns',
    slugs: ['full', 'semi', 'local'],
    moduleLabel: '退货退款',
    reason: '退货退款按店铺类型分流：全托退供包裹，半托/本土销售售后',
  },
  {
    prefix: 'templates/warehouse',
    slugs: ['semi', 'local'],
    moduleLabel: '仓库模板',
    reason: '发货仓按站点查询，只用于半托管/本土发品；全托管发品不选站点与发货仓',
  },
  {
    prefix: 'templates/freight',
    slugs: ['semi', 'local'],
    moduleLabel: '运费模板',
    reason: '运费模板只用于半托管/本土发品；全托管发品不选运费模板',
  },
  // 旧扁平链接（已重定向到 orders/*），保留拦截以免直链绕过
  { prefix: 'fulfillment', slugs: ['full'], moduleLabel: '备货发货', reason: '备货发货仅全托管店铺可用' },
  { prefix: 'print', slugs: ['full'], moduleLabel: '打印中心', reason: '打印中心仅全托管店铺可用' },
  { prefix: 'sales', slugs: ['full'], moduleLabel: '销量数据', reason: '销量数据仅全托管店铺可用' },
  { prefix: 'packing', slugs: ['full'], moduleLabel: '包装合规', reason: '包装合规仅全托管店铺可用' },
  { prefix: 'ads', slugs: ['full'], moduleLabel: '商品广告', reason: '商品广告仅全托管店铺可用' },
  { prefix: 'aftersale', slugs: ['full'], moduleLabel: '寄样质检', reason: '寄样质检仅全托管店铺可用' },
  { prefix: 'compliance/packing', slugs: ['full'], moduleLabel: '包装合规', reason: '包装合规仅全托管店铺可用' },
  { prefix: 'compliance/ads', slugs: ['full'], moduleLabel: '商品广告', reason: '商品广告仅全托管店铺可用' },
  { prefix: 'compliance/sample-qc', slugs: ['full'], moduleLabel: '寄样质检', reason: '寄样质检仅全托管店铺可用' },
];

/** 从 pathname 取相对 {@code /ipe/temu/{slug}/} 的路径；非 TEMU 路径返回 ''。 */
export function temuRelativePathFromPathname(pathname = '') {
  const p = String(pathname || '');
  const prefix = `${IPE_TEMU_BASE_PATH}/`;
  if (!p.startsWith(prefix)) return '';
  const rest = p.slice(prefix.length);
  const slash = rest.indexOf('/');
  if (slash < 0) return '';
  return rest.slice(slash + 1).replace(/^\/+|\/+$/g, '');
}

function matchGatedPath(relativePath) {
  const rel = String(relativePath || '').replace(/^\/+|\/+$/g, '');
  if (!rel) return null;
  let matched = null;
  for (const entry of TEMU_TRACK_GATED_PATHS) {
    if (rel === entry.prefix || rel.startsWith(`${entry.prefix}/`)) {
      if (!matched || entry.prefix.length > matched.prefix.length) {
        matched = entry;
      }
    }
  }
  return matched;
}

/** 该相对路径在指定店铺类型下是否可用。 */
export function isTemuRelativePathSupported(shopTypeSlug, relativePath) {
  const matched = matchGatedPath(relativePath);
  if (!matched) return true;
  const slug = normalizeTemuShopTypeSlug(shopTypeSlug) || 'semi';
  return matched.slugs.includes(slug);
}

/**
 * 路由级模块可用性判定。
 * @returns {{supported: boolean, moduleLabel?: string, reason?: string, allowedSlugs?: string[]}}
 */
export function resolveTemuModuleSupport(shopTypeSlug, pathname) {
  const matched = matchGatedPath(temuRelativePathFromPathname(pathname));
  if (!matched) return { supported: true };
  const slug = normalizeTemuShopTypeSlug(shopTypeSlug) || 'semi';
  if (matched.slugs.includes(slug)) return { supported: true };
  return {
    supported: false,
    moduleLabel: matched.moduleLabel,
    reason: matched.reason,
    allowedSlugs: matched.slugs.slice(),
  };
}

/** 按店铺类型返回模板库侧栏子项 */
export function templateSubnavForShopTypeSlug(shopTypeSlug) {
  const slug = normalizeTemuShopTypeSlug(shopTypeSlug) || 'semi';
  if (slug === 'full' || slug === 'local') {
    return TEMU_TEMPLATE_SUBNAV.filter((c) => !TEMU_TEMPLATE_SUBNAV_SEMI_ONLY_IDS.has(c.id));
  }
  return TEMU_TEMPLATE_SUBNAV;
}

/** 全量子项（含隐藏），供直链标题解析 */
export const TEMU_TEMPLATE_SUBNAV_ALL = [
  ...TEMU_TEMPLATE_SUBNAV,
  { id: 'rrp', label: '建议价资料库', path: 'rrp' },
];

export const TEMU_COMPLIANCE_SUBNAV = [
  { id: 'info', label: '合规信息', path: 'info' },
  { id: 'photos', label: '实拍图', path: 'photos' },
  { id: 'certs', label: '证书资质', path: 'certs' },
];

/** @deprecated 包装/广告/寄样已并入订单履约子导航 */
export const TEMU_COMPLIANCE_SUBNAV_FULL_EXTRA = [
  { id: 'packing', label: '包装合规', path: 'packing' },
  { id: 'ads', label: '商品广告', path: 'ads' },
  { id: 'sample-qc', label: '寄样质检', path: 'sample-qc' },
];

export const TEMU_PRICE_SUBNAV = [
  { id: 'cost', label: '核价单', path: 'cost' },
  { id: 'rrp', label: '调价单', path: 'rrp' },
];

export const TEMU_ORDER_SUBNAV = [
  { id: 'list', label: '订单列表', path: '' },
  { id: 'returns', label: '退货退款', path: 'returns' },
];

/**
 * 全托管履约子路由（路由 / 守卫 / 标题解析仍保留全量；侧栏暂时只露出备货发货）。
 */
export const TEMU_FULFILLMENT_ORDER_SUBNAV = [
  { id: 'stocking', label: '备货发货', path: 'stocking' },
  { id: 'print', label: '打印中心', path: 'print' },
  { id: 'sales', label: '销量数据', path: 'sales' },
  { id: 'packing', label: '包装合规', path: 'packing' },
  { id: 'ads', label: '商品广告', path: 'ads' },
  { id: 'sample-qc', label: '寄样质检', path: 'sample-qc' },
];

/** 全托侧栏可见的履约子项（其余隐藏，代码与路由保留） */
export const TEMU_FULFILLMENT_ORDER_SUBNAV_VISIBLE = TEMU_FULFILLMENT_ORDER_SUBNAV.filter(
  (c) => c.id === 'stocking',
);

export const TEMU_MARKETING_SUBNAV = [
  { id: 'activities', label: '活动列表', path: '' },
  { id: 'enrolls', label: '报名记录', path: 'enrolls' },
];

/** 半托 / 本土侧栏（不含全托履约专属项） */
export const TEMU_NAV_BASE = [
  { id: 'home', label: '首页', path: 'home' },
  {
    id: 'products',
    label: '产品管理',
    path: 'products',
    children: TEMU_PRODUCT_SUBNAV,
  },
  {
    id: 'discuss',
    label: '价格处理',
    path: 'discuss',
    children: TEMU_PRICE_SUBNAV,
  },
  {
    id: 'templates',
    label: '模板库',
    path: 'templates',
    children: TEMU_TEMPLATE_SUBNAV,
  },
  {
    id: 'marketing',
    label: '营销活动',
    path: 'marketing',
    children: TEMU_MARKETING_SUBNAV,
  },
  {
    id: 'compliance',
    label: '合规资质',
    path: 'compliance',
    children: TEMU_COMPLIANCE_SUBNAV,
  },
  {
    id: 'orders',
    label: '订单管理',
    path: 'orders',
    children: TEMU_ORDER_SUBNAV,
  },
  { id: 'shop', label: '店铺信息', path: 'shop' },
];

/**
 * 按店铺类型返回侧栏菜单。
 *
 * <p>顶层模块显隐同样走 {@link TEMU_TRACK_GATED_PATHS}（例如「价格处理」只对全托展示），
 * 子项再叠加全托专属履约子项与模板库子项裁剪，避免侧栏与路由守卫两套口径。
 *
 * @param {'full'|'semi'|'local'|string} [shopTypeSlug]
 */
export function navItemsForShopTypeSlug(shopTypeSlug) {
  const slug = normalizeTemuShopTypeSlug(shopTypeSlug) || 'semi';
  const templateChildren = templateSubnavForShopTypeSlug(slug);

  return TEMU_NAV_BASE
    .filter((item) => isTemuRelativePathSupported(slug, item.path))
    .map((item) => {
      if (item.id === 'templates') {
        return copyNavItem(item, { children: templateChildren });
      }
      if (item.id === 'orders') {
        if (slug === 'full') {
          return copyNavItem(item, { children: TEMU_FULFILLMENT_ORDER_SUBNAV_VISIBLE });
        }
        return copyNavItem(item, { children: TEMU_ORDER_SUBNAV });
      }
      if (slug !== 'full') return item;
      if (item.id === 'compliance') {
        return copyNavItem(item, { children: TEMU_COMPLIANCE_SUBNAV });
      }
      return item;
    });
}

/** @deprecated 旧全托扁平菜单，保留空数组以免外部引用报错 */
export const TEMU_NAV_FULL_EXTRA = [];

/** 兼容旧引用：默认半托可见菜单 */
export const TEMU_NAV_ITEMS = TEMU_NAV_BASE;

/** 隐藏路由标题解析用（不进侧栏） */
export const TEMU_HIDDEN_NAV_ITEMS = [
  { id: 'stock', label: '仓库库存', path: 'stock' },
  { id: 'price', label: '价格', path: 'price' },
  {
    id: 'orders-hidden',
    label: '订单管理',
    path: 'orders',
    children: TEMU_ORDER_SUBNAV.filter((c) => c.id !== 'list'),
  },
  {
    id: 'fulfillment-legacy',
    label: '订单管理',
    path: 'fulfillment',
    children: TEMU_FULFILLMENT_ORDER_SUBNAV,
  },
  {
    id: 'orders-fulfillment-hidden',
    label: '订单管理',
    path: 'orders',
    children: TEMU_FULFILLMENT_ORDER_SUBNAV.filter((c) => c.id !== 'stocking'),
  },
  { id: 'finance', label: '财务账单', path: 'finance' },
  {
    id: 'templates-hidden',
    label: '模板库',
    path: 'templates',
    children: TEMU_TEMPLATE_SUBNAV_ALL.filter(
      (c) => !TEMU_TEMPLATE_SUBNAV.some((v) => v.id === c.id),
    ),
  },
];

bindNavLabels([
  TEMU_PRODUCT_SUBNAV,
  TEMU_TEMPLATE_SUBNAV_ALL,
  TEMU_COMPLIANCE_SUBNAV,
  TEMU_COMPLIANCE_SUBNAV_FULL_EXTRA,
  TEMU_PRICE_SUBNAV,
  TEMU_ORDER_SUBNAV,
  TEMU_FULFILLMENT_ORDER_SUBNAV,
  TEMU_MARKETING_SUBNAV,
  TEMU_NAV_BASE,
  TEMU_HIDDEN_NAV_ITEMS,
]);

export { normalizeTemuShopType };

/** @param {string} [shopType] @returns {'full'|'semi'|'local'|''} */
export function shopTypeSlugForLabel(shopType) {
  const normalized = normalizeTemuShopType(shopType);
  if (normalized === '全托管') return 'full';
  if (normalized === '半托管') return 'semi';
  if (normalized === '本土') return 'local';
  return '';
}

/** @param {string} [slug] @returns {'全托管'|'半托管'|'本土'|''} */
export function shopTypeLabelForSlug(slug) {
  const s = String(slug || '').trim().toLowerCase();
  if (s === 'full') return '全托管';
  if (s === 'semi') return '半托管';
  if (s === 'local' || s === 'l2l') return '本土';
  return '';
}

/** @deprecated 兼容旧名 */
export function trackForShopType(shopType) {
  return shopTypeSlugForLabel(shopType);
}

/** @deprecated 兼容旧名 */
export function shopTypeForTrack(track) {
  return shopTypeLabelForSlug(track);
}

export function normalizeTemuShopTypeSlug(slug) {
  const label = shopTypeLabelForSlug(slug);
  return label ? shopTypeSlugForLabel(label) : '';
}

export function isTemuShopTypeSlug(slug) {
  return Boolean(normalizeTemuShopTypeSlug(slug));
}

export function isTemuShopAuthorized(shop) {
  if (!shop || shop.authStatus !== 'connected') return false;
  if (String(shop?.platformCode || '').toUpperCase() !== 'TEMU') return false;
  if (shop.designSandbox === true) return true;
  const identifier = String(shop?.shopIdentifier || '');
  if (identifier.startsWith('TEMU-DESIGN-')) return true;
  if (shop.mockBinding === true) return false;
  if (shop.realAuthBound === false) return false;
  return true;
}

function resolveTemuShopTypeSlug(shopTypeOrSlug) {
  if (isTemuShopTypeSlug(shopTypeOrSlug)) {
    return normalizeTemuShopTypeSlug(shopTypeOrSlug);
  }
  const fromLabel = shopTypeSlugForLabel(shopTypeOrSlug);
  if (fromLabel) return fromLabel;
  if (typeof window !== 'undefined') {
    const fromPath = parseTemuShopTypeSlugFromPath(window.location.pathname);
    if (fromPath) return fromPath;
  }
  try {
    if (typeof localStorage !== 'undefined') {
      const stored = localStorage.getItem(TEMU_WORKSPACE_SHOP_TYPE_KEY);
      const fromStored = shopTypeSlugForLabel(stored);
      if (fromStored) return fromStored;
    }
  } catch {
    /* ignore */
  }
  return 'semi';
}

/**
 * - buildTemuPath('home') — 从 URL / storage 推断类型
 * - buildTemuPath('full', 'home') — 显式类型
 */
export function buildTemuPath(shopTypeOrSegment = '', maybeSegment) {
  let slug;
  let segment;
  if (maybeSegment !== undefined) {
    slug = resolveTemuShopTypeSlug(shopTypeOrSegment);
    segment = maybeSegment;
  } else if (isTemuShopTypeSlug(shopTypeOrSegment) && !String(shopTypeOrSegment).includes('/')) {
    slug = normalizeTemuShopTypeSlug(shopTypeOrSegment);
    segment = '';
  } else {
    slug = resolveTemuShopTypeSlug('');
    segment = shopTypeOrSegment;
  }
  const base = `${IPE_TEMU_BASE_PATH}/${slug || 'semi'}`;
  if (!segment) return base;
  return `${base}/${String(segment).replace(/^\//, '')}`;
}

export function buildTemuChildPath(parentPath = '', childPath = '', shopTypeOrSlug = '') {
  const parent = String(parentPath || '').replace(/^\/+|\/+$/g, '');
  const slugArg = shopTypeOrSlug || undefined;
  if (!parent) {
    return slugArg !== undefined ? buildTemuPath(slugArg, childPath) : buildTemuPath(childPath);
  }
  if (!childPath) {
    return slugArg !== undefined ? buildTemuPath(slugArg, parent) : buildTemuPath(parent);
  }
  const seg = `${parent}/${String(childPath).replace(/^\//, '')}`;
  return slugArg !== undefined ? buildTemuPath(slugArg, seg) : buildTemuPath(seg);
}

export function buildTemuProductChildPath(childPath = '', shopTypeOrSlug = '') {
  return buildTemuChildPath('products', childPath, shopTypeOrSlug);
}

export function buildTemuComplianceChildPath(childPath = '', shopTypeOrSlug = '') {
  return buildTemuChildPath('compliance', childPath, shopTypeOrSlug);
}

export function buildTemuTemplateChildPath(childPath = '', shopTypeOrSlug = '') {
  return buildTemuChildPath('templates', childPath, shopTypeOrSlug);
}

export function buildTemuGatePath({ shopId, shopType, redirect } = {}) {
  const params = new URLSearchParams();
  if (shopId != null && String(shopId).trim() !== '') {
    params.set('shopId', String(shopId).trim());
  }
  if (shopType) {
    const label = normalizeTemuShopType(shopType) || shopTypeLabelForSlug(shopType) || String(shopType);
    if (label) params.set('shopType', label);
  }
  if (redirect) {
    params.set('redirect', String(redirect));
  }
  const qs = params.toString();
  return qs ? `${IPE_TEMU_GATE_PATH}?${qs}` : IPE_TEMU_GATE_PATH;
}

export function openTemuGateForShop(shopId, options = {}) {
  ensureTemuWorkspaceTokenShared();
  const path = buildTemuGatePath({
    shopId,
    shopType: options.shopType,
    redirect: options.redirect,
  });
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  openUrlInNewTab(`${origin}${path}`);
}

export function ensureTemuWorkspaceTokenShared() {
  try {
    if (typeof localStorage === 'undefined') return;
    if (localStorage.getItem(TEMU_WORKSPACE_TOKEN_KEY)) return;
    if (typeof sessionStorage === 'undefined') return;
    const fromSession = sessionStorage.getItem(TEMU_WORKSPACE_TOKEN_KEY);
    if (!fromSession) return;
    localStorage.setItem(TEMU_WORKSPACE_TOKEN_KEY, fromSession);
    sessionStorage.removeItem(TEMU_WORKSPACE_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

export function openTemuPathInNewTab(segmentOrPath = '') {
  ensureTemuWorkspaceTokenShared();
  const path = String(segmentOrPath || '').startsWith('/')
    ? String(segmentOrPath)
    : buildTemuPath(segmentOrPath);
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  openUrlInNewTab(`${origin}${path}`);
}

export function isTemuPlatformPath(pathname = '') {
  const p = pathname || '';
  return p === IPE_TEMU_BASE_PATH || p.startsWith(`${IPE_TEMU_BASE_PATH}/`);
}

/** 从 pathname 解析类型 slug */
export function parseTemuShopTypeSlugFromPath(pathname = '') {
  const p = String(pathname || '');
  const prefix = `${IPE_TEMU_BASE_PATH}/`;
  if (!p.startsWith(prefix)) return '';
  const rest = p.slice(prefix.length);
  const first = rest.split('/')[0] || '';
  if (first === 'gate' || first === 'help') return '';
  return normalizeTemuShopTypeSlug(first);
}

function navTitle(item) {
  return platformNavLabel(
    (key, opts) => i18n.t(key, { ns: 'workbench', defaultValue: '', ...opts }),
    item,
  );
}

/** 文档标题：根据 shopType + pathname 解析页面名 */
export function resolveTemuPageMeta(shopTypeOrSlug, pathname = '') {
  const p = String(pathname || '');
  const slug = normalizeTemuShopTypeSlug(shopTypeOrSlug)
    || parseTemuShopTypeSlugFromPath(p)
    || 'semi';
  const prefix = `${IPE_TEMU_BASE_PATH}/${slug}/`;
  let rest = p.startsWith(prefix) ? p.slice(prefix.length) : '';
  rest = rest.replace(/\/+$/, '');
  if (!rest || rest === 'home') {
    return {
      title: i18n.t('platformNav.home', { ns: 'workbench', defaultValue: '首页' }),
      shopType: shopTypeLabelForSlug(slug),
    };
  }
  const top = rest.split('/')[0] || '';
  const navPool = [
    ...navItemsForShopTypeSlug(slug),
    ...TEMU_HIDDEN_NAV_ITEMS,
  ];
  for (const item of navPool) {
    if (item.path === top) {
      if (Array.isArray(item.children) && item.children.length) {
        const childSeg = rest.slice(top.length).replace(/^\//, '');
        const child = item.children.find((c) => {
          if (!c.path) return childSeg === '' || childSeg === top;
          return childSeg === c.path || childSeg.startsWith(`${c.path}/`);
        });
        if (child) {
          return {
            title: `${navTitle(item)} · ${navTitle(child)}`,
            shopType: shopTypeLabelForSlug(slug),
          };
        }
      }
      return { title: navTitle(item), shopType: shopTypeLabelForSlug(slug) };
    }
  }
  return {
    title: i18n.t('platformNav.workspace', { ns: 'workbench', defaultValue: '工作区' }),
    shopType: shopTypeLabelForSlug(slug),
  };
}

/** @deprecated */
export function normalizeTemuTrack(track) {
  return normalizeTemuShopTypeSlug(track) || 'semi';
}
